import { DEFAULT_SITE } from './siteDefaults';

const SLIDE_INTERVAL_DEFAULT = 6500;
const SLIDE_INTERVAL_MIN = 2000;
const SLIDE_INTERVAL_MAX = 120000;

const HERO_FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1920&q=75';

const HERO_ICON_KEYS = new Set(['hayBale', 'corn', 'sprout']);

function strPrefer(a, b, fallback) {
  if (typeof a === 'string' && a.trim()) return a.trim();
  if (typeof b === 'string' && b.trim()) return b.trim();
  return typeof fallback === 'string' ? fallback : '';
}

function intervalMs(raw, fallbackRaw, hardDefault) {
  const n = Number(raw);
  if (Number.isFinite(n) && n >= SLIDE_INTERVAL_MIN && n <= SLIDE_INTERVAL_MAX) {
    return Math.round(n);
  }
  const n2 = Number(fallbackRaw);
  if (Number.isFinite(n2) && n2 >= SLIDE_INTERVAL_MIN && n2 <= SLIDE_INTERVAL_MAX) {
    return Math.round(n2);
  }
  const n3 = Number(hardDefault);
  return Number.isFinite(n3) ? n3 : SLIDE_INTERVAL_DEFAULT;
}

function ownString(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key) && typeof obj[key] === 'string';
}

/**
 * One carousel slide: image + its own headline, copy, CTA, and dwell time.
 */
function parseHeroSlide(raw, heroLevel, defaults, editMode) {
  let obj = raw;
  if (typeof raw === 'string') {
    const src = raw.trim();
    if (!src) return null;
    obj = { src, alt: '' };
  }
  if (!obj || typeof obj !== 'object') return null;
  const src = typeof obj.src === 'string' ? obj.src.trim() : '';
  if (!src) return null;
  const alt = typeof obj.alt === 'string' ? obj.alt.trim() : '';

  const slideIntervalMs = intervalMs(
    Number(obj.slideIntervalMs),
    heroLevel.slideIntervalMs,
    intervalMs(defaults.slideIntervalMs, null, SLIDE_INTERVAL_DEFAULT)
  );

  if (editMode) {
    const pick = (k, heroKey = k) => {
      if (ownString(obj, k)) return obj[k];
      if (typeof heroLevel[heroKey] === 'string') return heroLevel[heroKey];
      return '';
    };
    const subtitle = ownString(obj, 'subtitle')
      ? obj.subtitle
      : typeof heroLevel.subtitle === 'string'
        ? heroLevel.subtitle
        : '';
    return {
      src,
      alt,
      label: pick('label'),
      title: pick('title'),
      subtitle,
      ctaLabel: pick('ctaLabel'),
      ctaHref: pick('ctaHref'),
      slideIntervalMs,
    };
  }

  return {
    src,
    alt,
    label: strPrefer(ownString(obj, 'label') ? obj.label : '', heroLevel.label, defaults.label || ''),
    title: strPrefer(ownString(obj, 'title') ? obj.title : '', heroLevel.title, defaults.title || ''),
    subtitle: (() => {
      if (ownString(obj, 'subtitle') && obj.subtitle.trim()) return obj.subtitle;
      return strPrefer(
        heroLevel.subtitle,
        defaults.subtitle,
        'Sustainable practices from soil to harvest.'
      );
    })(),
    ctaLabel: strPrefer(
      ownString(obj, 'ctaLabel') ? obj.ctaLabel : '',
      heroLevel.ctaLabel,
      defaults.ctaLabel || 'Explore More'
    ),
    ctaHref: strPrefer(
      ownString(obj, 'ctaHref') ? obj.ctaHref : '',
      heroLevel.ctaHref,
      defaults.ctaHref || '#explore'
    ),
    slideIntervalMs,
  };
}

function buildBackgroundSlides(rawList, heroLevel, defaults, slideDefaultsList, editMode) {
  if (Array.isArray(rawList) && rawList.length === 0) return [];
  const list = Array.isArray(rawList) ? rawList : [];
  let out = list.map((x) => parseHeroSlide(x, heroLevel, defaults, editMode)).filter(Boolean);
  if (!out.length && !editMode && slideDefaultsList.length) {
    out = slideDefaultsList
      .map((x) => parseHeroSlide(x, heroLevel, defaults, editMode))
      .filter(Boolean);
  }
  return out;
}

const HIGHLIGHT_FALLBACK = [
  { line1: 'Healthy Soil', line2: 'Solutions', icon: 'hayBale' },
  { line1: 'Pure Organic', line2: 'Growth', icon: 'corn' },
  { line1: 'Nature-Driven', line2: 'Innovation', icon: 'sprout' },
];

function normalizeHighlights(raw, defaultsList) {
  if (Array.isArray(raw) && raw.length === 0) return [];
  const d = Array.isArray(defaultsList) && defaultsList.length ? defaultsList : HIGHLIGHT_FALLBACK;
  const list = Array.isArray(raw) && raw.length ? raw : d;
  const out = list.map((h, i) => {
    const def = d[i] || d[0] || HIGHLIGHT_FALLBACK[0];
    const icon =
      typeof h?.icon === 'string' && HERO_ICON_KEYS.has(h.icon) ? h.icon : def.icon;
    const line1 =
      h && typeof h === 'object' && typeof h.line1 === 'string'
        ? h.line1.trim()
        : strPrefer(h?.line1, def.line1, 'Highlight');
    const line2 =
      h && typeof h === 'object' && typeof h.line2 === 'string'
        ? h.line2.trim()
        : strPrefer(h?.line2, def.line2, '');
    return { line1, line2, icon };
  });
  const visible = out.filter((row) => row.line1 || row.line2);
  return visible.length ? visible : HIGHLIGHT_FALLBACK.map((x) => ({ ...x }));
}

/** Admin/editor: use API values as-is (including ""), no marketing fallbacks. */
function normalizeHighlightsEdit(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((h) => {
    const line1 = typeof h?.line1 === 'string' ? h.line1.trim() : '';
    const line2 = typeof h?.line2 === 'string' ? h.line2.trim() : '';
    const icon =
      typeof h?.icon === 'string' && HERO_ICON_KEYS.has(h.icon) ? h.icon : 'hayBale';
    const id = typeof h?.id === 'string' && h.id.trim() ? h.id.trim() : undefined;
    return { line1, line2, icon, ...(id ? { id } : {}) };
  });
}

/**
 * Hero copy, carousel slides, and highlight rows from API with defaults fallback.
 * Each background slide carries its own label, title, subtitle, CTAs, and interval.
 * Pass `{ editMode: true }` for the admin Hero form.
 */
export function resolveHero(site, options = {}) {
  const editMode = options.editMode === true;
  const defaults = DEFAULT_SITE.hero && typeof DEFAULT_SITE.hero === 'object' ? DEFAULT_SITE.hero : {};
  const h = site?.hero && typeof site.hero === 'object' ? site.hero : {};

  const explicitEmptySlides = Array.isArray(h.backgroundSlides) && h.backgroundSlides.length === 0;
  const slideDefaultsList = editMode
    ? []
    : Array.isArray(defaults.backgroundSlides)
      ? defaults.backgroundSlides
      : [];

  let backgroundSlides = buildBackgroundSlides(h.backgroundSlides, h, defaults, slideDefaultsList, editMode);

  if (!editMode && !explicitEmptySlides && !backgroundSlides.length) {
    const one = parseHeroSlide({ src: HERO_FALLBACK_IMAGE, alt: '' }, h, defaults, false);
    backgroundSlides = one ? [one] : [];
  }

  const highlights = editMode
    ? normalizeHighlightsEdit(h.highlights)
    : normalizeHighlights(h.highlights, defaults.highlights);

  if (editMode) {
    return {
      backgroundSlides,
      highlights,
    };
  }

  const first = backgroundSlides[0];
  const slideIntervalMs =
    first?.slideIntervalMs ??
    intervalMs(h.slideIntervalMs, defaults.slideIntervalMs, SLIDE_INTERVAL_DEFAULT);

  return {
    label: first?.label ?? '',
    title: first?.title ?? '',
    subtitle: first?.subtitle ?? '',
    ctaLabel: first?.ctaLabel ?? '',
    ctaHref: first?.ctaHref ?? '',
    slideIntervalMs,
    backgroundSlides,
    highlights,
  };
}
